import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { collectionGroup, collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FiRefreshCw, FiPlay, FiCheckCircle, FiXCircle, FiClock, FiChevronDown, FiChevronRight, FiSend } from 'react-icons/fi';
import { db, functions } from '../../config/firebase';
import { colors, typography } from '../../theme/colors';

// Aggregates featureAccessLog cross-company. Reads via collectionGroup so
// super admin can see every company's interaction with the agent. Provides
// a manual "Run agent now" button that calls runFeatureSuggestionsAgentNow.
//
// Permission: super admin only — caller is enforced by Firestore rules
// (collectionGroup query against featureAccessLog) AND by the
// runFeatureSuggestionsAgentNow Cloud Function checking the superAdmins
// collection.
const FeatureAdoptionTab = () => {
  const [rows, setRows] = useState([]);
  // companyId → companyName, used to resolve drill-down rows.
  const [companyNames, setCompanyNames] = useState({});
  // moduleId → array of pending+approved+declined requests for that module.
  const [requestsByModule, setRequestsByModule] = useState({});
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [agentResult, setAgentResult] = useState(null);
  const [periodDays, setPeriodDays] = useState(30);
  // Set of moduleIds whose drill-down row is currently expanded.
  const [expandedModules, setExpandedModules] = useState(new Set());

  const toggleExpand = (moduleId) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const since = new Date();
      since.setDate(since.getDate() - periodDays);

      // Pull access log + company names + active feature requests in
      // parallel. companyName lookup is best-effort — if a company doc was
      // deleted we just render the bare id.
      const [logSnap, companiesSnap, requestsSnap] = await Promise.all([
        getDocs(query(
          collectionGroup(db, 'featureAccessLog'),
          where('timestamp', '>=', since),
          orderBy('timestamp', 'desc'),
          limit(2000)
        )),
        getDocs(collection(db, 'companies')),
        getDocs(collection(db, 'featureRequests')),
      ]);

      // Each log doc lives at companies/{companyId}/featureAccessLog/{logId},
      // so the companyId is the parent-of-parent doc id. Without this we
      // can't drill down to "which company tried what".
      setRows(logSnap.docs.map(d => ({
        id: d.id,
        companyId: d.ref.parent?.parent?.id || null,
        ...d.data()
      })));

      const nameMap = {};
      companiesSnap.forEach(d => {
        const data = d.data();
        nameMap[d.id] = data.companyName || data.tradeName || d.id;
      });
      setCompanyNames(nameMap);

      const reqMap = {};
      requestsSnap.forEach(d => {
        const data = d.data();
        if (!data.moduleId) return;
        if (!reqMap[data.moduleId]) reqMap[data.moduleId] = [];
        reqMap[data.moduleId].push({ id: d.id, ...data });
      });
      setRequestsByModule(reqMap);
    } catch (err) {
      console.error('[FeatureAdoptionTab] load:', err);
      setError(err?.message || 'Failed to load feature access log.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [periodDays]);

  // Two-level pivot:
  //   moduleId → aggregate stats + per-company breakdown
  // The byCompany map drives the drill-down rendering: one expanded row per
  // company that interacted with this feature, sorted by trial activity so
  // the warmest leads bubble to the top.
  const pivot = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!r.moduleId) return;
      if (!map[r.moduleId]) {
        map[r.moduleId] = {
          moduleId: r.moduleId,
          views: 0, tries: 0, dismisses: 0,
          users: new Set(),
          companies: new Set(),
          byCompany: {},
          lastAt: null,
        };
      }
      const m = map[r.moduleId];
      if (r.action === 'view') m.views += 1;
      else if (r.action === 'try') m.tries += 1;
      else if (r.action === 'dismiss') m.dismisses += 1;
      if (r.userId) m.users.add(r.userId);
      if (r.companyId) {
        m.companies.add(r.companyId);
        if (!m.byCompany[r.companyId]) {
          m.byCompany[r.companyId] = {
            companyId: r.companyId,
            views: 0, tries: 0, dismisses: 0, requests: 0,
            users: new Set(), lastAt: null, lastTryAt: null,
          };
        }
        const c = m.byCompany[r.companyId];
        if (r.action === 'view') c.views += 1;
        else if (r.action === 'try') c.tries += 1;
        else if (r.action === 'dismiss') c.dismisses += 1;
        else if (r.action === 'request') c.requests += 1;
        if (r.userId) c.users.add(r.userId);
        const ts = r.timestamp?.toMillis ? r.timestamp.toMillis() : null;
        if (ts) {
          if (!c.lastAt || ts > c.lastAt) c.lastAt = ts;
          if (r.action === 'try' && (!c.lastTryAt || ts > c.lastTryAt)) c.lastTryAt = ts;
        }
      }
      const ts = r.timestamp?.toMillis ? r.timestamp.toMillis() : null;
      if (ts && (!m.lastAt || ts > m.lastAt)) m.lastAt = ts;
    });

    return Object.values(map)
      .map(m => {
        const byCompany = Object.values(m.byCompany)
          .map(c => ({ ...c, users: c.users.size }))
          .sort((a, b) => (b.lastTryAt || b.lastAt || 0) - (a.lastTryAt || a.lastAt || 0));
        const reqList = requestsByModule[m.moduleId] || [];
        return {
          ...m,
          users: m.users.size,
          companies: m.companies.size,
          byCompany,
          pendingRequests: reqList.filter(r => r.status === 'pending').length,
          totalRequests: reqList.length,
          conversion: m.views > 0 ? Math.round((m.tries / m.views) * 100) : 0,
        };
      })
      .sort((a, b) => b.tries - a.tries || b.views - a.views);
  }, [rows, requestsByModule]);

  // Helper: per-company request status for the drill-down row. Returns null
  // if no request exists, or the request object if pending/approved/declined.
  const getRequestForCompany = (moduleId, companyId) => {
    const list = requestsByModule[moduleId] || [];
    return list.find(r => r.companyId === companyId) || null;
  };

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      if (r.action === 'view') acc.views += 1;
      else if (r.action === 'try') acc.tries += 1;
      else if (r.action === 'dismiss') acc.dismisses += 1;
      return acc;
    }, { views: 0, tries: 0, dismisses: 0 });
  }, [rows]);

  const runAgent = async () => {
    setRunning(true);
    setAgentResult(null);
    setError(null);
    try {
      // Match the server-side timeout (540 s). The default client timeout is
      // only 70 s — a manual run across many companies (especially first
      // run, when feature descriptions need to be generated by Claude) often
      // exceeds that. The `timeout` option here is in milliseconds.
      const fn = httpsCallable(functions, 'runFeatureSuggestionsAgentNow', { timeout: 540000 });
      const res = await fn({});
      setAgentResult(res?.data || { ok: true });
    } catch (err) {
      console.error('[FeatureAdoptionTab] runAgent:', err);
      setError(err?.message || 'Agent run failed. Check Cloud Function logs.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Wrap>
      <Header>
        <HeaderLeft>
          <Title>Feature Adoption</Title>
          <Subtitle>Tracks how users respond to AI suggestions across all companies.</Subtitle>
        </HeaderLeft>
        <HeaderActions>
          <PeriodSelect value={periodDays} onChange={e => setPeriodDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </PeriodSelect>
          <Btn onClick={load} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} /> Reload
          </Btn>
          <Btn $primary onClick={runAgent} disabled={running}>
            <FiPlay /> {running ? 'Running…' : 'Run agent now'}
          </Btn>
        </HeaderActions>
      </Header>

      {error && <ErrorBox>{error}</ErrorBox>}
      {agentResult && (
        <SuccessBox>
          Agent run complete. {Array.isArray(agentResult.results)
            ? `${agentResult.results.length} companies processed.`
            : 'Result available in Cloud Function logs.'}
        </SuccessBox>
      )}

      <Stats>
        <Stat>
          <StatLabel>Total Views</StatLabel>
          <StatValue>{totals.views.toLocaleString()}</StatValue>
        </Stat>
        <Stat>
          <StatLabel>Trials Started</StatLabel>
          <StatValue style={{ color: colors.success.main }}>{totals.tries.toLocaleString()}</StatValue>
        </Stat>
        <Stat>
          <StatLabel>Dismissals</StatLabel>
          <StatValue style={{ color: colors.error.main }}>{totals.dismisses.toLocaleString()}</StatValue>
        </Stat>
        <Stat>
          <StatLabel>Conversion (try / view)</StatLabel>
          <StatValue>
            {totals.views > 0
              ? `${Math.round((totals.tries / totals.views) * 100)}%`
              : '—'}
          </StatValue>
        </Stat>
      </Stats>

      <TableWrap>
        <Table>
          <thead>
            <Tr>
              <Th style={{ width: 32 }}></Th>
              <Th>Feature</Th>
              <Th $right>Views</Th>
              <Th $right>Tries</Th>
              <Th $right>Dismisses</Th>
              <Th $right>Companies</Th>
              <Th $right>Requests</Th>
              <Th $right>Conversion</Th>
              <Th>Last Activity</Th>
            </Tr>
          </thead>
          <tbody>
            {pivot.length === 0 && !loading && (
              <Tr><Td colSpan={9} style={{ textAlign: 'center', color: colors.text.secondary, padding: 24 }}>
                No feature interactions in this period yet.
              </Td></Tr>
            )}
            {pivot.map(row => {
              const isExpanded = expandedModules.has(row.moduleId);
              return (
                <React.Fragment key={row.moduleId}>
                  <Tr $clickable onClick={() => toggleExpand(row.moduleId)}>
                    <Td style={{ width: 32, paddingRight: 0 }}>
                      {row.byCompany.length > 0 && (
                        isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />
                      )}
                    </Td>
                    <Td><ModuleName>{row.moduleId}</ModuleName></Td>
                    <Td $right>{row.views}</Td>
                    <Td $right style={{ color: colors.success.main, fontWeight: 700 }}>
                      {row.tries > 0 && <FiCheckCircle size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                      {row.tries}
                    </Td>
                    <Td $right style={{ color: row.dismisses > 0 ? colors.error.main : colors.text.secondary }}>
                      {row.dismisses > 0 && <FiXCircle size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                      {row.dismisses}
                    </Td>
                    <Td $right>{row.companies}</Td>
                    <Td $right>
                      {row.pendingRequests > 0 && (
                        <RequestBadge>
                          <FiSend size={10} /> {row.pendingRequests} pending
                        </RequestBadge>
                      )}
                      {row.pendingRequests === 0 && row.totalRequests > 0 && (
                        <MutedCount>{row.totalRequests} total</MutedCount>
                      )}
                      {row.totalRequests === 0 && <MutedCount>—</MutedCount>}
                    </Td>
                    <Td $right>
                      <ConvBar>
                        <ConvFill style={{ width: `${row.conversion}%` }} />
                        <ConvLabel>{row.conversion}%</ConvLabel>
                      </ConvBar>
                    </Td>
                    <Td>
                      {row.lastAt
                        ? <span><FiClock size={11} style={{ verticalAlign: 'middle' }} /> {new Date(row.lastAt).toLocaleString()}</span>
                        : '—'}
                    </Td>
                  </Tr>
                  {isExpanded && row.byCompany.length > 0 && (
                    <Tr>
                      <Td colSpan={9} style={{ padding: '0 0 0 32px', background: colors.background?.paper || '#fafafa' }}>
                        <DrillDown>
                          <DrillHeader>
                            <span>Companies that interacted with <strong>{row.moduleId}</strong></span>
                          </DrillHeader>
                          <DrillTable>
                            <thead>
                              <Tr>
                                <Th>Company</Th>
                                <Th $right>Views</Th>
                                <Th $right>Tries</Th>
                                <Th $right>Users</Th>
                                <Th>Last Try</Th>
                                <Th>Request Status</Th>
                              </Tr>
                            </thead>
                            <tbody>
                              {row.byCompany.map(c => {
                                const req = getRequestForCompany(row.moduleId, c.companyId);
                                return (
                                  <Tr key={c.companyId}>
                                    <Td>{companyNames[c.companyId] || c.companyId}</Td>
                                    <Td $right>{c.views}</Td>
                                    <Td $right style={{ color: c.tries > 0 ? colors.success.main : colors.text.secondary, fontWeight: 600 }}>
                                      {c.tries}
                                    </Td>
                                    <Td $right>{c.users}</Td>
                                    <Td>
                                      {c.lastTryAt
                                        ? new Date(c.lastTryAt).toLocaleString()
                                        : <MutedCount>never</MutedCount>}
                                    </Td>
                                    <Td>
                                      {!req && <MutedCount>none</MutedCount>}
                                      {req?.status === 'pending' && <ReqStatusPill $tone="pending">Pending</ReqStatusPill>}
                                      {req?.status === 'approved' && <ReqStatusPill $tone="approved">Approved</ReqStatusPill>}
                                      {req?.status === 'declined' && <ReqStatusPill $tone="declined">Declined</ReqStatusPill>}
                                    </Td>
                                  </Tr>
                                );
                              })}
                            </tbody>
                          </DrillTable>
                        </DrillDown>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </Wrap>
  );
};

export default FeatureAdoptionTab;

// ── styled ──────────────────────────────────────────────────────────────────
const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  padding: 16px 0;
`;
const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end; gap: 12px;
  flex-wrap: wrap;
`;
const HeaderLeft = styled.div``;
const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.text.primary};
`;
const Subtitle = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
  margin-top: 4px;
`;
const HeaderActions = styled.div`
  display: flex; align-items: center; gap: 8px;
`;
const PeriodSelect = styled.select`
  padding: 7px 10px;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  background: white;
`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px;
  background: ${p => p.$primary ? colors.primary.main : 'white'};
  color: ${p => p.$primary ? 'white' : colors.text.primary};
  border: 1px solid ${p => p.$primary ? colors.primary.main : colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: ${p => p.$primary ? colors.primary.dark || colors.primary.main : colors.background?.paper || '#fafafa'};
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;
const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;
const Stat = styled.div`
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  padding: 12px 14px;
`;
const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
`;
const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily.mono};
  margin-top: 4px;
`;
const TableWrap = styled.div`
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;
const Tr = styled.tr`
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  &:hover { background: ${colors.background?.paper || '#fafafa'}; }
`;
const Th = styled.th`
  padding: 10px 12px;
  text-align: ${p => p.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
  background: ${colors.background?.paper || '#fafafa'};
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
`;
const Td = styled.td`
  padding: 10px 12px;
  text-align: ${p => p.$right ? 'right' : 'left'};
  border-bottom: 1px solid ${colors.neutral?.[100] || '#f3f4f6'};
  color: ${colors.text.primary};
  font-weight: 500;
`;
const ModuleName = styled.span`
  font-family: ${typography.fontFamily.mono};
  font-weight: 600;
  color: ${colors.primary.main};
`;
const ConvBar = styled.div`
  position: relative;
  width: 100%;
  height: 18px;
  background: ${colors.neutral?.[100] || '#f3f4f6'};
  border-radius: 9px;
  overflow: hidden;
`;
const ConvFill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  background: ${colors.success.main};
  opacity: 0.85;
`;
const ConvLabel = styled.span`
  position: absolute;
  inset: 0;
  display: inline-flex; align-items: center; justify-content: flex-end;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily.mono};
`;
const ErrorBox = styled.div`
  padding: 10px 14px;
  background: ${colors.error.main}10;
  border: 1px solid ${colors.error.main};
  border-radius: 4px;
  color: ${colors.error.main};
  font-size: 13px;
  font-weight: 500;
`;
const SuccessBox = styled.div`
  padding: 10px 14px;
  background: ${colors.success.main}10;
  border: 1px solid ${colors.success.main};
  border-radius: 4px;
  color: ${colors.success.main};
  font-size: 13px;
  font-weight: 500;
`;
const RequestBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: ${colors.warning.main}18;
  color: ${colors.warning.main};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
`;
const MutedCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
`;
const DrillDown = styled.div`
  padding: 12px 16px 16px;
  border-left: 3px solid ${colors.primary.main}50;
`;
const DrillHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  margin-bottom: 8px;
  strong { color: ${colors.primary.main}; font-family: ${typography.fontFamily.mono}; }
`;
const DrillTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 4px;
  overflow: hidden;
  th, td { padding: 8px 10px; }
`;
const ReqStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 700;
  ${p => {
    const tone = p.$tone || 'pending';
    const c = tone === 'approved' ? colors.success.main
      : tone === 'declined' ? colors.error.main
      : colors.warning.main;
    return `color: ${c}; background: ${c}18;`;
  }}
`;
